import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Spinner, Collapse } from 'react-bootstrap'
import { weighbridgeSerialReadUrl } from '../../generic/crud'
import { defaultCamera, defaultWeighbridge, PARSER_PRESETS } from '../parserPresets'
import { showToast, TOAST_FAILURE } from '../../../utils/toast/Toast'
import CameraConfigSection from './CameraConfigSection'
import WeighbridgeConfigSection from './WeighbridgeConfigSection'

const groupId = (g) => g && (g.primaryId || g.id)

// Merge server-provided group data over the device defaults so camera/weighbridge are
// never null (a fresh setup can return a group without them). Applied both at initial
// render and on re-sync so the child sections always receive complete objects.
const normalizeGroup = (g) => ({
    ...g,
    camera: { ...defaultCamera(), ...((g && g.camera) || {}) },
    weighbridge: { ...defaultWeighbridge(), ...((g && g.weighbridge) || {}) },
})

// One setup group = ONE camera + ONE weighbridge. Owns the editable draft, exposes
// name/enable/delete/save controls, and hosts both device sections.
const SetupGroupCard = ({ group, expanded, onToggle, onSave, onDelete }) => {
    const [draft, setDraft] = useState(() => normalizeGroup(group))
    const [saving, setSaving] = useState(false)

    // Re-sync the draft when the persisted group identity changes (e.g. after save/reload).
    useEffect(() => {
        setDraft(normalizeGroup(group))
    }, [groupId(group)])

    const updateField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }))
    const updateCamera = (field, value) => setDraft(prev => ({ ...prev, camera: { ...prev.camera, [field]: value } }))
    const updateWb = (field, value) => setDraft(prev => ({ ...prev, weighbridge: { ...prev.weighbridge, [field]: value } }))
    const handlePreset = (typeValue) => {
        const preset = PARSER_PRESETS.find(p => p.value === typeValue)
        setDraft(prev => ({ ...prev, weighbridge: { ...prev.weighbridge, weighbridgeType: typeValue, parserFunction: preset ? preset.parser : prev.weighbridge.parserFunction } }))
    }

    const validate = () => {
        if (!draft.name || !draft.name.trim()) { showToast('Group name is required', TOAST_FAILURE); return false }
        if (!draft.weighbridge.brandName || !draft.weighbridge.brandName.trim()) { showToast('Weighbridge brand name is required', TOAST_FAILURE); return false }
        if (!draft.weighbridge.serialPort || !draft.weighbridge.serialPort.trim()) { showToast('Serial port is required', TOAST_FAILURE); return false }
        return true
    }

    // Persist the whole draft (camera + weighbridge). Returns the saved group or null.
    const persist = async () => {
        setSaving(true)
        try {
            return await onSave(draft)
        } finally {
            setSaving(false)
        }
    }

    const handleSave = async () => {
        if (!validate()) return
        await persist()
    }

    // Serial monitor needs a persisted id — save first, then build the SSE url.
    const getMonitorUrl = async () => {
        if (!validate()) return null
        const saved = await persist()
        const id = groupId(saved || draft)
        if (!id) { showToast('Could not resolve group — save failed', TOAST_FAILURE); return null }
        return weighbridgeSerialReadUrl(id)
    }

    return (
        <Card className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-white py-3 px-4 border-bottom">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: '260px' }}>
                        <Button variant="link" className="p-0 me-2 text-muted" onClick={onToggle}>
                            <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'} fs-5`}></i>
                        </Button>
                        <Form.Control size="sm" type="text" value={draft.name || ''} onChange={(e) => updateField('name', e.target.value)}
                            placeholder="Group name (e.g. Scale 1)" style={{ maxWidth: '260px' }} />
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <Form.Check type="switch" id={`grp-enabled-${groupId(group) || 'new'}`}
                            label={<span className={`small fw-semibold ${draft.enabled ? 'text-success' : 'text-muted'}`}>{draft.enabled ? 'Enabled' : 'Disabled'}</span>}
                            checked={!!draft.enabled} onChange={(e) => updateField('enabled', e.target.checked)} />
                        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                            {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving</> : <><i className="bi bi-check-lg me-1"></i>Save</>}
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => onDelete(group)} disabled={saving}>
                            <i className="bi bi-trash"></i>
                        </Button>
                    </div>
                </div>
            </Card.Header>
            <Collapse in={expanded}>
                <div>
                    <Card.Body className="p-4">
                        <CameraConfigSection camera={draft.camera} onChange={updateCamera} />
                        <WeighbridgeConfigSection config={draft.weighbridge} onChange={updateWb} onPresetChange={handlePreset} getMonitorUrl={getMonitorUrl} />
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    )
}

export default SetupGroupCard
